import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getCurrentUser, getSession, signOut as cognitoSignOut } from "./cognito";

interface AuthState {
  loading: boolean;
  email: string | null;
  isAuthenticated: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    const session = await getSession();
    if (session) {
      const claims = session.getIdToken().payload as Record<string, unknown>;
      setEmail((claims.email as string) ?? (getCurrentUser()?.getUsername() ?? null));
    } else {
      setEmail(null);
    }
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      email,
      isAuthenticated: !!email,
      refresh,
      signOut: () => {
        cognitoSignOut();
        setEmail(null);
      },
    }),
    [loading, email]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
